import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTimeEastern, formatEventDateTime, timeAgo } from '../utils/dates';
import { isAdminLike, isSuperAdmin } from '../utils/roles';
import { TM_ACTIVE, tmErrorText } from '../hooks/useTicketMetric';

// "TicketMetric sold" strip on Compare: realized StubHub + SeatGeek sales for
// this event, pulled straight from TicketMetric through our own login and
// proxy (services/ticketMetric). Data comes from useTicketMetric (`tm` prop).
//
// Times: lastPulledAt / lastSold / pausedUntil are UTC instants → Eastern.
// TicketMetric event dates (localDateTime) are venue-local wall clock and are
// shown verbatim, never converted.

const money = (v) => (v == null || !Number.isFinite(Number(v)) ? '-' : `$${Number(v).toFixed(2)}`);
const count = (v) => (v == null || !Number.isFinite(Number(v)) ? '-' : Number(v).toLocaleString('en-US'));
const instant = (v) => (v ? formatDateTimeEastern(v, { year: undefined }) : '-');

const PHASE_TEXT = {
  starting: 'Starting…',
  searching: 'Searching TicketMetric…',
  pulling: 'Pulling sales…',
  stats: 'Updating stats…',
  siblings: 'Finding sister dates…',
};
const METHOD_TEXT = {
  stubhub: 'matched by StubHub link',
  date_city: 'matched by date + city',
  vivid: 'matched by Vivid link',
  manual: 'picked by hand',
};
const WARNING_TEXT = {
  ALPHA_MISMATCH: 'letter suffix differs (e.g. 336A vs 336)',
  AMBIGUOUS: 'several TicketMetric sections fit equally',
  SHARED_TP: 'another of our sections maps to the same TicketMetric section',
};

// Live "resuming in Ns" for a TicketMetric pause
function PauseCountdown({ until }) {
  const target = Date.parse(until) || 0;
  const secsLeft = () => Math.max(0, Math.ceil((target - Date.now()) / 1000));
  const [left, setLeft] = useState(secsLeft);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.ceil((target - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [target]);
  return <>{left > 0 ? `resuming in ${left}s` : 'resuming…'}</>;
}

function ActiveStatus({ view }) {
  if (view.status === 'paused') {
    return (
      <span title={view.pauseReason || undefined}>
        TicketMetric asked us to slow down — {view.pausedUntil ? <PauseCountdown until={view.pausedUntil} /> : 'resuming shortly'}
      </span>
    );
  }
  if (view.status === 'queued') return <span>{view.position ? `Queued #${view.position}` : 'Queued'}</span>;
  return <span>{PHASE_TEXT[view.phase] || 'Working…'}</span>;
}

// Low confidence = anything weaker than "same number, same suffix", a
// warning from the matcher, or a whole-zone fallback
function matchInfo(s) {
  if (s.tpSection) {
    const low = (s.confidence != null && s.confidence < 0.75)
      || (s.warnings || []).some(w => w === 'ALPHA_MISMATCH' || w === 'AMBIGUOUS');
    const parts = [`${s.method || 'name'} match${s.confidence != null ? `, ${Math.round(s.confidence * 100)}% confidence` : ''}`];
    for (const w of s.warnings || []) parts.push(WARNING_TEXT[w] || w);
    if (s.alternates?.length) parts.push(`also fits: ${s.alternates.join(', ')}`);
    return { low, title: parts.join(' · ') };
  }
  if (s.zone) return { low: true, title: `No section match — the numbers are for the whole "${s.zone.name}" zone` };
  return { low: false, title: 'No TicketMetric sales matched this section' };
}

function SectionsTable({ view }) {
  const days = view.windowDays || 30;
  const rows = view.ourSections || [];
  if (!rows.length) {
    const zones = view.zones || [];
    if (!zones.length) return <div className="sales-empty">No section-level sales yet.</div>;
    return (
      <>
        <div className="tm-note">By TicketMetric zone (our sections show once Skybox pricing loads).</div>
        <table className="sales-table tm-table">
          <thead>
            <tr><th>Zone</th><th>Sales</th><th>Median</th><th>p25–p75</th><th>Last sold</th><th>Last {days}d</th></tr>
          </thead>
          <tbody>
            {zones.map(z => (
              <tr key={z.className}>
                <td>{z.className}</td>
                <td title={z.all?.qty != null ? `${count(z.all.qty)} tickets` : undefined}>{count(z.all?.n)}</td>
                <td>{money(z.all?.median)}</td>
                <td>{z.all ? `${money(z.all.p25)}–${money(z.all.p75)}` : '-'}</td>
                <td title={z.lastSold ? timeAgo(z.lastSold) : undefined}>{instant(z.lastSold)}</td>
                <td>{z.win?.n > 0 ? `${z.win.n} · ${money(z.win.median)}` : '0'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }
  return (
    <table className="sales-table tm-table">
      <thead>
        <tr>
          <th>Our section</th>
          <th>TicketMetric section</th>
          <th>Sales</th>
          <th>Median</th>
          <th>p25–p75</th>
          <th>Last sold</th>
          <th title={`Sales in the last ${days} days: count · median`}>Last {days}d</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(s => {
          const z = s.zone;
          const all = s.all || z?.all || null;
          const win = s.all ? s.win : (z?.win || null);
          const lastSold = s.all ? s.lastSold : (z?.lastSold || null);
          const m = matchInfo(s);
          return (
            <tr key={s.ours} className={m.low ? 'tm-row-low' : undefined}>
              <td>{s.ours}</td>
              <td title={m.title}>
                {s.tpSection
                  ? <>{s.tpSection}{s.className ? <span className="tm-muted"> · {s.className}</span> : null}</>
                  : z ? <>zone: {z.name}</> : <span className="tm-muted">no sales matched</span>}
                {m.low && <span className="tm-chip tm-chip-warn" title={m.title}>≈ check</span>}
              </td>
              <td title={all?.qty != null ? `${count(all.qty)} tickets` : undefined}>{all ? count(all.n) : '-'}</td>
              <td>{money(all?.median)}</td>
              <td>{all ? `${money(all.p25)}–${money(all.p75)}` : '-'}</td>
              <td title={lastSold ? timeAgo(lastSold) : undefined}>{instant(lastSold)}</td>
              <td>{win?.n > 0 ? `${win.n} · ${money(win.median)}` : (all ? '0' : '-')}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CandidateHints({ c }) {
  const hints = [];
  if (c.vividMatch) hints.push(<span key="vivid" className="tm-chip tm-chip-good">Vivid match</span>);
  if (c.stubhubMatch) hints.push(<span key="sh" className="tm-chip tm-chip-good">StubHub match</span>);
  if (c.sameDate === false) hints.push(<span key="date" className="tm-chip tm-chip-warn">different date</span>);
  if (c.placeConflict) hints.push(<span key="place" className="tm-chip tm-chip-bad" title="TicketMetric's city or venue disagrees with ours">city/venue differs</span>);
  if (c.penalized) hints.push(<span key="pen" className="tm-chip tm-chip-warn" title="Add-on listings share the main show's StubHub link">looks like {(c.penalties || []).join(' / ') || 'an add-on'}</span>);
  if (c.timeDeltaMin != null && c.timeDeltaMin > 15) hints.push(<span key="time" className="tm-chip tm-chip-warn">starts {c.timeDeltaMin} min apart</span>);
  if (c.rejected) hints.push(<span key="rej" className="tm-chip" title="Someone clicked Wrong event? on this one">marked wrong before</span>);
  return <>{hints}</>;
}

function CandidateList({ candidates, tm, onError }) {
  const [busyId, setBusyId] = useState(null);
  const use = async (c) => {
    setBusyId(c.tmetricId);
    onError(null);
    try {
      await tm.link({ tmetricId: c.tmetricId });
    } catch (err) {
      onError(tmErrorText(err, 'Failed to link the TicketMetric event'));
    } finally {
      setBusyId(null);
    }
  };
  return (
    <table className="sales-table tm-table tm-candidates">
      <thead>
        <tr><th>TicketMetric event</th><th>Venue</th><th>Date (local)</th><th>Sold</th><th></th><th></th></tr>
      </thead>
      <tbody>
        {candidates.map(c => (
          <tr key={c.tmetricId} className={c.rejected ? 'tm-row-rejected' : undefined}>
            <td>{c.name || c.tmetricId}</td>
            <td>{[c.venue, c.city].filter(Boolean).join(' · ') || '-'}</td>
            <td>{c.localDateTime ? formatEventDateTime(c.localDateTime) : '-'}</td>
            <td>{count(c.totalSold)}</td>
            <td><span className="tm-hints"><CandidateHints c={c} /></span></td>
            <td style={{ textAlign: 'right' }}>
              <button className="btn-secondary btn-sm" onClick={() => use(c)} disabled={!!busyId || tm.linking}>
                {busyId === c.tmetricId ? 'Saving…' : 'Use this event'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SearchBox({ tm }) {
  const [q, setQ] = useState('');
  const [note, setNote] = useState(null); // { text, error? }
  const submit = async (e) => {
    e.preventDefault();
    const term = q.trim();
    if (term.length < 2 || tm.searching) return;
    setNote(null);
    try {
      const r = await tm.search(term);
      if (r?.status === 'done') {
        setNote(r.candidates?.length ? null : { text: `No TicketMetric events found for “${term}”.` });
      } else {
        setNote({ text: `TicketMetric is busy — the search is queued${r?.position ? ` (#${r.position})` : ''}. Search again in a moment to see the results.` });
      }
    } catch (err) {
      setNote({ text: tmErrorText(err, 'Search failed'), error: true });
    }
  };
  return (
    <>
      <form className="tm-input-row" onSubmit={submit}>
        <input
          type="text"
          className="ai-ask-input"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search TicketMetric (performer, venue, city…)"
          maxLength={100}
          disabled={tm.searching}
        />
        <button type="submit" className="btn-secondary btn-sm" disabled={tm.searching || q.trim().length < 2}>
          {tm.searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      {note && <div className={note.error ? 'ai-inline-error' : 'tm-note'}>{note.text}</div>}
    </>
  );
}

function PasteBox({ tm }) {
  const [value, setValue] = useState('');
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v || tm.linking) return;
    setErr(null);
    try {
      await tm.link(/^[0-9a-f]{24}$/i.test(v) ? { tmetricId: v } : { url: v });
      setValue('');
    } catch (e2) {
      setErr(tmErrorText(e2, 'Failed to link the TicketMetric event'));
    }
  };
  return (
    <>
      <form className="tm-input-row" onSubmit={submit}>
        <input
          type="text"
          className="ai-ask-input"
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="…or paste the TicketMetric event link or its 24-character id"
          maxLength={500}
          disabled={tm.linking}
        />
        <button type="submit" className="btn-secondary btn-sm" disabled={tm.linking || !value.trim()}>
          {tm.linking ? 'Saving…' : 'Use this link'}
        </button>
      </form>
      {err && <div className="ai-inline-error">{err}</div>}
    </>
  );
}

/**
 * Props:
 *   tm   — return value of useTicketMetric(eventId, { sections })
 *   role — the viewer's role (pricers never see the "not connected" strip)
 */
function TicketMetricPanel({ tm, role }) {
  const [open, setOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const view = tm.view;
  const status = view?.status || null;

  const refresh = async () => {
    setActionError(null);
    try {
      await tm.refresh();
    } catch (err) {
      setActionError(tmErrorText(err, 'TicketMetric refresh failed'));
    }
  };
  const wrongEvent = async () => {
    if (!window.confirm('Unlink this TicketMetric event? It will never be picked automatically for this event again, and TicketMetric is searched again — you may be asked to pick the right one.')) return;
    setActionError(null);
    try {
      await tm.unlink();
    } catch (err) {
      setActionError(tmErrorText(err, 'Failed to unlink the TicketMetric event'));
    }
  };

  const shell = ({ meta, chips, actions, body, toggle }) => (
    <div className="compare-sales-section compare-tm-section">
      <div
        className="sales-header tm-header"
        onClick={toggle ? () => setOpen(o => !o) : undefined}
        style={{ cursor: toggle ? 'pointer' : 'default' }}
      >
        {toggle && <span className="sales-toggle">{open ? '▼' : '▶'}</span>}
        <span className="tm-title">TicketMetric sold</span>
        {meta && <span className="tm-meta">{meta}</span>}
        {chips}
        {actions && <span className="tm-actions" onClick={e => e.stopPropagation()}>{actions}</span>}
      </div>
      {body && <div className="sales-content tm-content">{body}</div>}
    </div>
  );

  // ---- nothing known yet ----
  if (!view) {
    if (tm.loading) return null; // a split second; no flash for pricers on an unconnected install
    const msg = actionError || (tm.error ? tmErrorText(tm.error, 'TicketMetric data unavailable') : tm.requestError);
    if (!msg) return null;
    const retry = async () => {
      setActionError(null);
      try {
        await tm.retry();
      } catch (err) {
        setActionError(tmErrorText(err, 'TicketMetric request failed'));
      }
    };
    return shell({
      meta: <span className="tm-error-text">{msg}</span>,
      actions: (
        <button className="btn-secondary btn-sm" onClick={retry} disabled={tm.retrying}>
          {tm.retrying ? 'Retrying…' : 'Retry'}
        </button>
      ),
    });
  }

  if (status === 'not_configured') {
    if (!isAdminLike(role)) return null;
    return shell({
      meta: isSuperAdmin(role)
        ? <>TicketMetric not connected — connect in <Link to="/admin?tab=ticketmetric" target="_blank" rel="noopener noreferrer">Admin → TicketMetric</Link></>
        : 'TicketMetric not connected',
    });
  }

  const active = TM_ACTIVE.has(status);
  // summary exists as soon as the linked event's row does; numbers only after a pull
  const summary = view.lastPulledAt ? view.summary : null;
  const link = view.link;
  const linked = link?.status === 'linked';
  const hasTable = !!summary && ((view.ourSections || []).length > 0 || (view.zones || []).length > 0);

  const refreshButton = (
    <button
      className="btn-secondary btn-sm tm-refresh"
      onClick={refresh}
      disabled={tm.refreshing || (active && !tm.stalled)}
      title={view.frozen ? 'The event is over — the numbers are final' : 'Pull fresh TicketMetric data now (data under 10 minutes old is otherwise reused)'}
    >
      {tm.refreshing ? '…' : '↻'}
    </button>
  );

  const activeChip = active ? (
    <span className={`tm-chip ${status === 'paused' ? 'tm-chip-warn' : 'tm-chip-info'}`}>
      <ActiveStatus view={view} />
    </span>
  ) : null;

  const stalledLine = tm.stalled ? (
    <div className="tm-note">
      Still waiting on TicketMetric. <button className="btn-link-inline" onClick={tm.recheck}>Check again</button>
    </div>
  ) : null;

  const errorLines = (
    <>
      {actionError && <div className="ai-inline-error">{actionError}</div>}
      {tm.requestError && !actionError && <div className="ai-inline-error">{tm.requestError}</div>}
    </>
  );

  const matchedLine = linked ? (
    <div className="tm-matched">
      <span className="tm-muted">Matched:</span>{' '}
      {link.event
        ? <>
            <b>{link.event.name || link.tmetricId}</b>
            {[link.event.venue, link.event.city].filter(Boolean).map(x => ` · ${x}`).join('')}
            {link.event.localDateTime ? ` · ${formatEventDateTime(link.event.localDateTime)}` : ''}
          </>
        : <>TicketMetric event {link.tmetricId}</>}
      {link.method && (
        <span className="tm-muted" title={link.linkedAt ? `linked ${instant(link.linkedAt)}` : undefined}>
          {' '}· {METHOD_TEXT[link.method] || link.method}{link.method === 'manual' && link.linkedByName ? ` by ${link.linkedByName}` : ''}
        </span>
      )}
      <button className="btn-secondary btn-sm tm-wrong" onClick={wrongEvent} disabled={tm.unlinking}>
        {tm.unlinking ? 'Unlinking…' : 'Wrong event?'}
      </button>
    </div>
  ) : null;

  // ---- data (ready, or a refresh running over earlier numbers) ----
  if (summary) {
    const s = summary;
    const meta = (
      <>
        {view.lastPulledAt ? <span title={timeAgo(view.lastPulledAt)}>updated {instant(view.lastPulledAt)}</span> : null}
        {` · sold 24h ${count(s.sold24h)} · 7d ${count(s.sold7d)} · all-time ${count(s.soldAll ?? s.nSales)}`}
        {` · get-in ${money(s.getIn)} · ATP ${money(s.atp)}`}
        {s.pctCapacity != null ? ` · resold ${Number(s.pctCapacity).toFixed(1)}% of capacity` : ''}
      </>
    );
    const matched = (view.ourSections || []).filter(x => x.tpSection || x.zone).length;
    return shell({
      toggle: hasTable,
      meta,
      chips: (
        <>
          {view.frozen && <span className="tm-chip" title="The event ended — no more pulls">event over — final numbers</span>}
          {activeChip}
        </>
      ),
      actions: refreshButton,
      body: (
        <>
          {matchedLine}
          {view.error && (
            <div className="tm-warn">
              Last TicketMetric pull failed{view.error.at ? ` (${instant(view.error.at)})` : ''}: {view.error.message} — showing the last good numbers.
            </div>
          )}
          {stalledLine}
          {errorLines}
          {!open && hasTable && (
            <button className="btn-link-inline tm-expand" onClick={() => setOpen(true)}>
              {(view.ourSections || []).length
                ? `Show sold by section (${matched} of ${view.ourSections.length} of our sections matched · ${count(s.nSales)} sales on file)`
                : `Show sold by zone (${count(s.nSales)} sales on file)`}
            </button>
          )}
          {open && hasTable && <SectionsTable view={view} />}
          {!hasTable && <div className="tm-note">No individual sales on file for this event yet.</div>}
        </>
      ),
    });
  }

  // ---- in progress, no earlier numbers ----
  if (active) {
    return shell({
      meta: <ActiveStatus view={view} />,
      actions: refreshButton,
      body: (matchedLine || stalledLine || actionError || tm.requestError) ? <>{matchedLine}{stalledLine}{errorLines}</> : null,
    });
  }

  if (status === 'needs_pick') {
    const candidates = view.candidates || [];
    return shell({
      meta: 'Which TicketMetric event is this? Pick one — it is saved for everyone.',
      actions: refreshButton,
      body: (
        <>
          {errorLines}
          {candidates.length > 0
            ? <CandidateList candidates={candidates} tm={tm} onError={setActionError} />
            : <div className="tm-note">No candidates stored — search below.</div>}
          <SearchBox tm={tm} />
          <PasteBox tm={tm} />
        </>
      ),
    });
  }

  if (status === 'not_found') {
    const candidates = view.candidates || [];
    return shell({
      meta: 'No matching TicketMetric event found for this date.',
      actions: refreshButton,
      body: (
        <>
          <div className="tm-note">
            Paste the event's TicketMetric link or id, or search for it.
            {link?.searchedAt ? ` Last searched ${timeAgo(link.searchedAt)}; it searches again on its own after 6 h.` : ''}
          </div>
          {errorLines}
          {candidates.length > 0 && <CandidateList candidates={candidates} tm={tm} onError={setActionError} />}
          <PasteBox tm={tm} />
          <SearchBox tm={tm} />
        </>
      ),
    });
  }

  if (status === 'error') {
    return shell({
      meta: <span className="tm-error-text">{view.error?.message || 'The last TicketMetric pull failed.'}</span>,
      actions: (
        <button className="btn-secondary btn-sm" onClick={refresh} disabled={tm.refreshing}>
          {tm.refreshing ? 'Retrying…' : 'Retry'}
        </button>
      ),
      body: (matchedLine || actionError || tm.requestError) ? <>{matchedLine}{errorLines}</> : null,
    });
  }

  // idle / ready-without-numbers: linked (or not yet searched) but nothing on file
  return shell({
    meta: status === 'ready' ? 'No TicketMetric sales on file for this event yet.' : 'No TicketMetric data yet.',
    actions: refreshButton,
    body: (matchedLine || actionError || tm.requestError) ? <>{matchedLine}{errorLines}</> : null,
  });
}

export default TicketMetricPanel;
